import { useCallback, useEffect, useRef, useState } from 'react';
import {
  deleteComment,
  getComments,
  saveComment,
  updateComment
} from 'integration';

export interface Comment {
  id: string;
  compareId: string;
  commentText: string;
  anchorData?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AnchorData {
  startXPath: string;
  startOffset: number;
  endXPath: string;
  endOffset: number;
  selectedText: string;
  viewType?: string; // which tab the annotation was created in
}

export interface PendingSelection {
  anchorData: string;
  position: { top: number; left: number };
}

// ─── XPath utilities ────────────────────────────────────────────────────────

function getXPath(node: Node): string {
  if (node.nodeType === Node.DOCUMENT_NODE) return '';

  if (node.nodeType === Node.TEXT_NODE) {
    const parent = node.parentNode as Element;
    const siblings = Array.from(parent.childNodes).filter(
      (n) => n.nodeType === Node.TEXT_NODE
    );
    const idx = siblings.indexOf(node as ChildNode);
    const suffix = siblings.length > 1 ? `[${idx + 1}]` : '';
    return `${getXPath(parent)}/text()${suffix}`;
  }

  if (node.nodeType === Node.ELEMENT_NODE) {
    const el = node as Element;
    const parent = el.parentNode;
    if (!parent) return `/${el.tagName.toLowerCase()}`;
    const siblings = Array.from(parent.childNodes).filter(
      (n) =>
        n.nodeType === Node.ELEMENT_NODE &&
        (n as Element).tagName === el.tagName
    );
    const idx = siblings.indexOf(el as ChildNode);
    const suffix = siblings.length > 1 ? `[${idx + 1}]` : '';
    return `${getXPath(parent)}/${el.tagName.toLowerCase()}${suffix}`;
  }

  return '';
}

function getNodeByXPath(xpath: string, doc: Document): Node | null {
  try {
    const result = doc.evaluate(
      xpath,
      doc,
      null,
      XPathResult.FIRST_ORDERED_NODE_TYPE,
      null
    );
    return result.singleNodeValue;
  } catch {
    return null;
  }
}

function serializeRange(range: Range, _doc: Document): AnchorData {
  return {
    startXPath: getXPath(range.startContainer),
    startOffset: range.startOffset,
    endXPath: getXPath(range.endContainer),
    endOffset: range.endOffset,
    selectedText: range.toString().slice(0, 200)
  };
}

function deserializeRange(anchor: AnchorData, doc: Document): Range | null {
  const startNode = getNodeByXPath(anchor.startXPath, doc);
  const endNode = getNodeByXPath(anchor.endXPath, doc);
  if (!startNode || !endNode) return null;
  try {
    const range = doc.createRange();
    range.setStart(startNode, anchor.startOffset);
    range.setEnd(endNode, anchor.endOffset);
    return range;
  } catch {
    return null;
  }
}

// ─── Highlight rendering ─────────────────────────────────────────────────────

function wrapTextNodes(range: Range, commentId: string, doc: Document): void {
  const walker = doc.createTreeWalker(
    range.commonAncestorContainer,
    NodeFilter.SHOW_TEXT,
    null
  );

  const textNodes: Text[] = [];
  let node = walker.nextNode() as Text;
  while (node) {
    if (range.intersectsNode(node)) {
      textNodes.push(node);
    }
    node = walker.nextNode() as Text;
  }

  textNodes.forEach((textNode, i) => {
    const nodeRange = doc.createRange();
    if (i === 0 && textNodes.length > 1) {
      nodeRange.setStart(textNode, range.startOffset);
      nodeRange.setEnd(textNode, textNode.length);
    } else if (i === textNodes.length - 1 && textNodes.length > 1) {
      nodeRange.setStart(textNode, 0);
      nodeRange.setEnd(textNode, range.endOffset);
    } else {
      nodeRange.selectNodeContents(textNode);
    }
    const mark = doc.createElement('mark');
    mark.dataset.commentId = commentId;
    mark.className = 'annotation-highlight';
    try {
      nodeRange.surroundContents(mark);
    } catch {
      // skip if range crosses element boundaries in a way that can't be wrapped
    }
  });
}

function applyHighlight(
  anchor: AnchorData,
  commentId: string,
  doc: Document
): void {
  const range = deserializeRange(anchor, doc);
  if (!range) return;

  if (range.startContainer === range.endContainer) {
    const mark = doc.createElement('mark');
    mark.dataset.commentId = commentId;
    mark.className = 'annotation-highlight';
    try {
      range.surroundContents(mark);
    } catch {
      wrapTextNodes(range, commentId, doc);
    }
  } else {
    wrapTextNodes(range, commentId, doc);
  }
}

function removeHighlight(commentId: string, doc: Document): void {
  const marks = Array.from(
    doc.querySelectorAll(`mark[data-comment-id="${commentId}"]`)
  );
  marks.forEach((mark) => {
    const parent = mark.parentNode;
    if (!parent) return;
    while (mark.firstChild) {
      parent.insertBefore(mark.firstChild, mark);
    }
    parent.removeChild(mark);
    parent.normalize();
  });
}

function injectHighlightStyles(doc: Document): void {
  if (doc.getElementById('annotation-styles')) return;
  const style = doc.createElement('style');
  style.id = 'annotation-styles';
  // Use underline + transparent background so existing red/blue change colors show through
  style.textContent = `
    mark.annotation-highlight {
      background: transparent;
      text-decoration: underline;
      text-decoration-color: rgba(0, 91, 168, 0.85);
      text-decoration-thickness: 2px;
      text-decoration-style: solid;
      cursor: pointer;
      border-radius: 0;
      padding: 0;
    }
    mark.annotation-highlight:hover {
      background: rgba(0, 91, 168, 0.12);
    }
  `;
  (doc.head || doc.documentElement).appendChild(style);
}

function sortCommentsByPosition(
  comments: Comment[],
  doc: Document | null
): Comment[] {
  if (!doc || comments.length <= 1) return comments;
  return [...comments].sort((a, b) => {
    if (!a.anchorData || !b.anchorData) return 0;
    try {
      const anchorA: AnchorData = JSON.parse(a.anchorData);
      const anchorB: AnchorData = JSON.parse(b.anchorData);
      const nodeA = getNodeByXPath(anchorA.startXPath, doc);
      const nodeB = getNodeByXPath(anchorB.startXPath, doc);
      if (!nodeA || !nodeB) return 0;
      const pos = nodeA.compareDocumentPosition(nodeB);
      if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
      if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
      return anchorA.startOffset - anchorB.startOffset;
    } catch {
      return 0;
    }
  });
}

// ─── View labels (shared with sidebar + download) ───────────────────────────

export const VIEW_LABELS: Record<string, string> = {
  combined: 'Combined View',
  outline: 'Outline View',
  sideBySide: 'Side by Side View'
};

// ─── Download builder ────────────────────────────────────────────────────────

export function buildDownloadHtml(
  htmlContent: string,
  comments: Comment[],
  activeTab: string
): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');

  injectHighlightStyles(doc);

  // Apply highlights for current-view comments only; track display index
  let refIndex = 0;
  const indexMap: Record<string, number> = {};

  comments.forEach((comment) => {
    if (!comment.anchorData) return;
    try {
      const anchor: AnchorData = JSON.parse(comment.anchorData);
      if (anchor.viewType && anchor.viewType !== activeTab) return;
      applyHighlight(anchor, comment.id, doc);
      refIndex += 1;
      indexMap[comment.id] = refIndex;
      // Append superscript ref after the last mark for this comment
      const marks = doc.querySelectorAll(
        `mark[data-comment-id="${comment.id}"]`
      );
      if (marks.length > 0) {
        const sup = doc.createElement('sup');
        sup.className = 'annotation-ref';
        sup.textContent = `[${refIndex}]`;
        marks[marks.length - 1].appendChild(sup);
      }
    } catch {
      // skip
    }
  });

  if (comments.length === 0) {
    return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
  }

  const escape = (s: string): string =>
    s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br>');

  const panelItems = comments
    .map((c) => {
      let anchor: AnchorData | null = null;
      try {
        anchor = c.anchorData ? JSON.parse(c.anchorData) : null;
      } catch {
        /* skip */
      }
      const isCurrentView = !anchor?.viewType || anchor.viewType === activeTab;
      const viewLabel =
        !isCurrentView && anchor?.viewType
          ? VIEW_LABELS[anchor.viewType] ?? anchor.viewType
          : null;
      const idx = indexMap[c.id];
      const badgeHtml = viewLabel
        ? `<span class="aep-badge">${viewLabel}</span> `
        : '';
      const numHtml = idx ? `<span class="aep-num">[${idx}]</span> ` : '';
      const quoteHtml = anchor?.selectedText
        ? `<blockquote class="aep-quote">&ldquo;${escape(
            anchor.selectedText
          )}&rdquo;</blockquote>`
        : '';
      const textHtml = `<p class="aep-text">${escape(c.commentText)}</p>`;
      return `<li class="aep-item" id="aep-item-${c.id}">${badgeHtml}${numHtml}${quoteHtml}${textHtml}</li>`;
    })
    .join('\n');

  const panelHtml = `
<style>
  #aep{margin:3rem 0 0;padding:1.5rem 2rem;border-top:3px solid #005ba8;font-family:system-ui,-apple-system,sans-serif;font-size:.9rem;color:#111}
  #aep h2{color:#005ba8;margin:0 0 1rem;font-size:1.1rem}
  .aep-list{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:.75rem}
  .aep-item{padding:.75rem 1rem;border:1px solid #e5e7eb;border-radius:.375rem;border-left:3px solid #005ba8;scroll-margin-top:1rem}
  .aep-item--active{background:#eff6ff;border-left-color:#1d4ed8}
  .aep-badge{display:inline-block;font-size:.7rem;font-weight:600;padding:.1rem .4rem;border-radius:.25rem;background:#dbeafe;color:#1d4ed8;text-transform:uppercase;margin-bottom:.4rem}
  .aep-num{font-weight:600;color:#005ba8}
  .aep-quote{margin:.25rem 0;padding:0 0 0 .75rem;border-left:2px solid #93c5fd;color:#4b5563;font-style:italic;font-size:.85rem}
  .aep-text{margin:.35rem 0 0;white-space:pre-wrap}
  sup.annotation-ref{font-size:.7em;color:#005ba8;font-weight:600;margin-left:1px}
</style>
<div id="aep">
  <h2>Annotations (${comments.length})</h2>
  <ol class="aep-list">
${panelItems}
  </ol>
</div>
<script>
(function(){
  document.querySelectorAll('mark.annotation-highlight').forEach(function(mark){
    mark.addEventListener('click',function(){
      var id=mark.dataset.commentId;
      var target=document.getElementById('aep-item-'+id);
      if(!target)return;
      document.querySelectorAll('.aep-item').forEach(function(el){el.classList.remove('aep-item--active');});
      target.classList.add('aep-item--active');
      target.scrollIntoView({behavior:'smooth',block:'center'});
    });
  });
})();
</script>`;

  doc.body.insertAdjacentHTML('beforeend', panelHtml);

  return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useAnnotations(
  comparisonId: string,
  businessArea: string,
  service: string,
  iframeRef: React.RefObject<HTMLIFrameElement>,
  activeTab: string
) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [pendingSelection, setPendingSelection] =
    useState<PendingSelection | null>(null);
  const [newCommentText, setNewCommentText] = useState('');
  const [editingComment, setEditingComment] = useState<{
    id: string;
    text: string;
  } | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [highlightedCommentId, setHighlightedCommentId] = useState<
    string | null
  >(null);

  // Keep refs so callbacks always read the latest values without stale closures
  const commentsRef = useRef<Comment[]>([]);
  commentsRef.current = comments;

  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;

  const iframeDocRef = useRef<Document | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  const loadComments = useCallback(async () => {
    try {
      const result = await getComments(businessArea, service, comparisonId);
      setComments(
        sortCommentsByPosition(result.comments ?? [], iframeDocRef.current)
      );
    } catch {
      // error toast shown by api layer
    }
  }, [comparisonId, businessArea, service]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  // Re-apply highlights whenever comments change after the iframe is already loaded
  useEffect(() => {
    const doc = iframeDocRef.current;
    if (!doc || !comments.length) return;
    comments.forEach((c) => {
      if (!c.anchorData) return;
      try {
        const anchor: AnchorData = JSON.parse(c.anchorData);
        if (anchor.viewType && anchor.viewType !== activeTabRef.current) return;
        applyHighlight(anchor, c.id, doc);
      } catch {
        // invalid anchor_data — skip
      }
    });
  }, [comments]);

  const applyAllHighlights = (doc: Document, tabType: string) => {
    injectHighlightStyles(doc);
    commentsRef.current.forEach((c) => {
      if (!c.anchorData) return;
      try {
        const anchor: AnchorData = JSON.parse(c.anchorData);
        if (anchor.viewType && anchor.viewType !== tabType) return;
        applyHighlight(anchor, c.id, doc);
      } catch {
        // invalid anchor_data — skip
      }
    });
  };

  const handleIframeMouseUp = useCallback(
    (doc: Document) => {
      const selection = doc.getSelection();
      if (!selection || selection.isCollapsed || !selection.toString().trim()) {
        return;
      }

      const range = selection.getRangeAt(0);
      const selRect = range.getBoundingClientRect();
      if (!iframeRef.current) return;

      const iframeRect = iframeRef.current.getBoundingClientRect();
      const anchor: AnchorData = {
        ...serializeRange(range, doc),
        viewType: activeTabRef.current
      };

      setPendingSelection({
        anchorData: JSON.stringify(anchor),
        position: {
          top: iframeRect.top + selRect.bottom + 8 + window.scrollY,
          left:
            iframeRect.left + selRect.left + selRect.width / 2 + window.scrollX
        }
      });
    },
    [iframeRef]
  );

  const onIframeLoad = useCallback(() => {
    if (!iframeRef.current?.contentDocument) return;
    const doc = iframeRef.current.contentDocument;
    iframeDocRef.current = doc;

    // Re-sort now that doc is available for position comparison
    setComments((prev) => sortCommentsByPosition(prev, doc));

    // Cleanup previous listeners if any
    if (cleanupRef.current) cleanupRef.current();

    applyAllHighlights(doc, activeTabRef.current);

    const mouseupHandler = () => handleIframeMouseUp(doc);
    const clickHandler = (e: MouseEvent) => {
      // Only open sidebar on a plain click (no text selected)
      const sel = doc.getSelection();
      if (sel && !sel.isCollapsed) return;
      const mark = (e.target as HTMLElement).closest('mark[data-comment-id]');
      if (!mark) return;
      const commentId = (mark as HTMLElement).dataset.commentId ?? null;
      setSidebarOpen(true);
      setHighlightedCommentId(commentId);
      setTimeout(() => setHighlightedCommentId(null), 2000);
    };

    doc.addEventListener('mouseup', mouseupHandler);
    doc.addEventListener('click', clickHandler);
    cleanupRef.current = () => {
      doc.removeEventListener('mouseup', mouseupHandler);
      doc.removeEventListener('click', clickHandler);
    };
  }, [handleIframeMouseUp]);

  // Cleanup listener on unmount
  useEffect(() => {
    return () => {
      if (cleanupRef.current) cleanupRef.current();
    };
  }, []);

  const cancelSelection = useCallback(() => {
    setPendingSelection(null);
    setNewCommentText('');
  }, []);

  const confirmAnnotation = useCallback(async () => {
    if (!newCommentText.trim() || !pendingSelection) return;
    try {
      const result = await saveComment(
        businessArea,
        service,
        comparisonId,
        newCommentText.trim(),
        pendingSelection.anchorData
      );
      const saved: Comment = result.comment;
      setComments((prev) =>
        sortCommentsByPosition([...prev, saved], iframeDocRef.current)
      );

      // Apply highlight immediately
      const doc = iframeDocRef.current;
      if (doc && saved.anchorData) {
        try {
          const anchor: AnchorData = JSON.parse(saved.anchorData);
          applyHighlight(anchor, saved.id, doc);
        } catch {
          // skip
        }
      }

      setPendingSelection(null);
      setNewCommentText('');
    } catch {
      // error toast shown by api layer
    }
  }, [newCommentText, pendingSelection, businessArea, service, comparisonId]);

  const updateAnnotation = useCallback(
    async (id: string, text: string) => {
      try {
        const result = await updateComment(
          businessArea,
          service,
          comparisonId,
          id,
          text
        );
        const updated: Comment = result.comment;
        setComments((prev) => prev.map((c) => (c.id === id ? updated : c)));
        setEditingComment(null);
      } catch {
        // error toast shown by api layer
      }
    },
    [businessArea, service, comparisonId]
  );

  const deleteAnnotation = useCallback(
    async (id: string) => {
      try {
        await deleteComment(businessArea, service, comparisonId, id);
        setComments((prev) => prev.filter((c) => c.id !== id));
        const doc = iframeDocRef.current;
        if (doc) removeHighlight(id, doc);
      } catch {
        // error toast shown by api layer
      }
    },
    [businessArea, service, comparisonId]
  );

  const scrollToAnnotation = useCallback((commentId: string) => {
    const doc = iframeDocRef.current;
    if (!doc) return;
    const mark = doc.querySelector(`mark[data-comment-id="${commentId}"]`);
    if (mark) mark.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  return {
    comments,
    pendingSelection,
    newCommentText,
    setNewCommentText,
    editingComment,
    setEditingComment,
    sidebarOpen,
    setSidebarOpen,
    highlightedCommentId,
    onIframeLoad,
    cancelSelection,
    confirmAnnotation,
    updateAnnotation,
    deleteAnnotation,
    scrollToAnnotation
  };
}
